import { useEffect, useRef, useState } from "preact/hooks";

import { useVideoAd } from "../presentation/VideoAdContext";
import { pushAdStage } from "../services/ad-stage-tracker";
import metrics from "../services/init-metrics";
import { loadScript } from "../utils/loadScript";
import { ImaController } from "./ima-controller";

const imaUrl = `https://imasdk.googleapis.com/js/sdkloader/ima3${location.href.includes("ark_ima_debug=true") ? "_debug" : ""}.js`;
const loadIma = () => loadScript(imaUrl, 8000);

export default function IMAPlayer() {
  const { session, state } = useVideoAd();
  const videoRef = useRef<HTMLVideoElement>(null);
  const displayContainerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<ImaController | null>(null);
  const startRequestedRef = useRef(false);
  const volumeRef = useRef(state.videoVolume);
  const [imaReady, setImaReady] = useState(false);
  volumeRef.current = state.videoVolume;

  useEffect(() => {
    session.markPlayerLoaded();
  }, [session]);

  useEffect(
    () =>
      session.registerPlaybackControls({
        start: () => {
          startRequestedRef.current = true;
          controllerRef.current?.start();
        },
        pause: () => controllerRef.current?.pause(),
        resume: () => controllerRef.current?.resume(),
      }),
    [session],
  );

  useEffect(() => {
    controllerRef.current?.setVolume(state.videoVolume);
  }, [state.videoVolume]);

  useEffect(() => {
    const video = videoRef.current;
    const displayContainer = displayContainerRef.current;
    if (!video || !displayContainer) return;

    const onAdContainerClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      session.togglePlayback();
    };
    displayContainer.addEventListener("click", onAdContainerClick);

    let disposed = false;

    void loadIma()
      .then(() => {
        if (disposed) return;
        const { ima } = window.google;
        ima.settings.setVpaidMode(ima.ImaSdkSettings.VpaidMode.INSECURE);
        controllerRef.current = new ImaController(
          ima,
          video,
          displayContainer,
          {
            onManagerLoaded: () => pushAdStage("ads_manager_loaded"),
            onContentPauseRequested: () => video.pause(),
            onContentResumeRequested: () => {
              controllerRef.current?.destroyManager();
              session.setPaused(false);
              pushAdStage("completed");
              void session.videoCompleted().catch(() => undefined);
            },
            onLoaded: (event) => {
              pushAdStage("loaded");
              metrics.trackRenderComplete("video-ad");
              document.dispatchEvent(new CustomEvent("ark-video-loaded"));
              if (!event.getAd().isLinear()) {
                void video.play().catch(() => undefined);
              }
            },
            onStarted: () => pushAdStage("started"),
            onPaused: () => session.setPaused(true),
            onResumed: () => session.setPaused(false),
            onSkipped: () => {
              document.dispatchEvent(new CustomEvent("ark-video-skipped"));
              pushAdStage("skipped");
            },
            onError: (error) => {
              controllerRef.current?.destroyManager();
              pushAdStage("error");
              void session.videoError(error, { player: "IMA" }).catch(() => undefined);
            },
          },
        );
        controllerRef.current.setVolume(state.videoVolume);
        if (startRequestedRef.current) controllerRef.current.start();
        setImaReady(true);
      })
      .catch(() => session.markImaFailed());

    return () => {
      disposed = true;
      displayContainer.removeEventListener("click", onAdContainerClick);
      controllerRef.current?.destroy();
      controllerRef.current = null;
    };
  }, [session]);

  useEffect(() => {
    if (!imaReady || !state.videoUrl) return;
    const controller = controllerRef.current;
    if (!controller) return;

    pushAdStage("ads_request");
    controller.request({
      adTagUrl: state.videoUrl,
      contentTitle: document.title,
      pageUrl: location.href,
      autoPlay: state.config.autoplay,
      muted: !volumeRef.current,
    });
  }, [imaReady, state.config.autoplay, state.videoUrl]);

  useEffect(() => {
    const resize = () => controllerRef.current?.resize(window.google.ima.ViewMode.NORMAL);
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const opacity = state.phase === "video" ? 1 : 0;

  return (
    <div className="vast-media">
      <video
        ref={videoRef}
        style={{
          width: "100%",
          opacity,
          transition: "opacity 1s ease-in",
          backgroundColor: "black",
        }}
        controls={false}
        playsInline
        src="https://ams.cdn.arkadiumhosted.com/advertisement/video/loader_black.mp4"
        muted={!state.videoVolume}
      >
        Sorry, your browser doesn't support embedded videos.
      </video>
      <div
        className="display-ad-container"
        ref={displayContainerRef}
        style={{ width: "100%", position: "absolute", left: 0, top: 0 }}
      />
    </div>
  );
}
